import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Calendar, ChevronDown, ChevronRight, Download, FileSpreadsheet, Info, Loader2, Ruler } from 'lucide-react';
import { useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { useConfirm } from '../shared/ConfirmDialog';
import { useVolumeMetadata } from './useVolumeMetadata';
import { VolumeMetadataPanel } from './VolumeMetadataPanel';
import { WorkbookEditor } from './WorkbookEditor';
/**
 * Workspace for workbook volumes (gov XLSX templates — Cost Volume, etc.).
 *
 * Two modes:
 *   - No workbook loaded → full metadata + load template card (landing view)
 *   - Workbook exists → compact collapsible info strip + full-pane editor
 */ export function WorkbookWorkspace({ projectId, volume }) {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const meta = useVolumeMetadata(projectId, volume);
    const [loadResult, setLoadResult] = useState(null);
    const sourceAttachmentId = volume.sourceAttachmentRefs?.[0]?.attachmentId || '';
    // Check if a workbook version exists (lightweight — just checks DB, no XLSX download).
    const { data: hasWorkbook, isLoading: checkingWorkbook } = useQuery({
        queryKey: [
            'workbookExists',
            projectId,
            volume.volumeId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listWorkbookVersions({
                projectId,
                volumeId: volume.volumeId,
                limit: 1
            });
            return (resp.versions?.length ?? 0) > 0;
        },
        enabled: !!projectId && !!volume.volumeId
    });
    const loadTemplate = useMutation({
        mutationFn: async ()=>{
            if (!sourceAttachmentId) throw new Error('No source attachment');
            return projectDataClient.loadTemplateIntoWorkbook({
                projectId,
                volumeId: volume.volumeId,
                attachmentId: sourceAttachmentId
            });
        },
        onSuccess: (resp)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'workbookSheets',
                    projectId,
                    volume.volumeId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'workbook',
                    projectId,
                    volume.volumeId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'workbookExists',
                    projectId,
                    volume.volumeId
                ]
            });
            const count = resp.sheetNames?.length ?? 0;
            setLoadResult(`Loaded ${count} sheet${count === 1 ? '' : 's'} (v${resp.version}) with full formatting`);
            setTimeout(()=>setLoadResult(null), 5000);
        },
        onError: (err)=>{
            console.error('Failed to load template:', err);
            setLoadResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setTimeout(()=>setLoadResult(null), 5000);
        }
    });
    const handleLoadTemplate = async ()=>{
        const ok = await confirm({
            title: 'Load government template?',
            description: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "This will parse the attached XLSX spreadsheet, preserving merged cells, column widths, and frozen rows/columns."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-slate-500",
                        children: "Any existing sheets for this volume will be replaced."
                    })
                ]
            }),
            variant: 'info',
            confirmLabel: 'Load template'
        });
        if (ok) loadTemplate.mutate();
    };
    if (checkingWorkbook) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Loader2, {
                className: "h-5 w-5 animate-spin text-slate-400"
            })
        });
    }
    // No workbook yet → full landing view with metadata + load template
    if (!hasWorkbook) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full flex-col bg-slate-50",
            children: /*#__PURE__*/ _jsx("div", {
                className: "flex-1 overflow-auto",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mx-auto w-full max-w-5xl p-4 sm:p-6 lg:p-8",
                    children: [
                        /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                            projectId: projectId,
                            volume: volume
                        }),
                        sourceAttachmentId && /*#__PURE__*/ _jsx(LoadTemplateCard, {
                            onLoad: handleLoadTemplate,
                            loading: loadTemplate.isPending
                        }),
                        loadResult && /*#__PURE__*/ _jsx(ResultToast, {
                            message: loadResult
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-8 flex items-center justify-center",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "max-w-md rounded-xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center",
                                children: [
                                    /*#__PURE__*/ _jsx(FileSpreadsheet, {
                                        className: "mx-auto h-8 w-8 text-slate-300"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-3 text-sm font-medium text-slate-700",
                                        children: "No spreadsheet yet"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1 text-xs text-slate-500",
                                        children: sourceAttachmentId ? 'Load the government template above to get started.' : 'Upload a pricing spreadsheet via Sources / Files.'
                                    })
                                ]
                            })
                        })
                    ]
                })
            })
        });
    }
    // Workbook exists → compact strip + full-pane editor
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col bg-slate-50",
        children: [
            /*#__PURE__*/ _jsx(CompactWorkbookStrip, {
                projectId: projectId,
                volume: volume,
                meta: meta,
                sourceAttachmentId: sourceAttachmentId,
                onLoadTemplate: handleLoadTemplate,
                loadingTemplate: loadTemplate.isPending
            }),
            loadResult && /*#__PURE__*/ _jsx("div", {
                className: "px-4 sm:px-6",
                children: /*#__PURE__*/ _jsx(ResultToast, {
                    message: loadResult
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 overflow-auto px-4 py-4 sm:px-6",
                children: /*#__PURE__*/ _jsx(WorkbookEditor, {
                    projectId: projectId,
                    volumeId: volume.volumeId
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Compact strip — mirrors the prose workspace's FormatBar
// ---------------------------------------------------------------------------
function CompactWorkbookStrip({ projectId, volume, meta, sourceAttachmentId, onLoadTemplate, loadingTemplate }) {
    const [showMeta, setShowMeta] = useState(false);
    const pageLimit = meta.pageLimits.find((p)=>p.isCurrentVersion) ?? meta.pageLimits[0];
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex-shrink-0 border-b border-slate-200 bg-white",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 px-3 py-2 sm:px-4",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>setShowMeta((v)=>!v),
                        className: "inline-flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-slate-50",
                        children: [
                            /*#__PURE__*/ _jsx(BookOpen, {
                                className: "h-3.5 w-3.5 flex-shrink-0 text-slate-400"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "min-w-0 truncate text-sm font-medium text-slate-800",
                                children: volume.displayName
                            }),
                            showMeta ? /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3 w-3 flex-shrink-0 text-slate-400"
                            }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "h-3 w-3 flex-shrink-0 text-slate-400"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "rounded border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] font-medium uppercase text-slate-600",
                                children: volume.fileFormat || 'XLSX'
                            }),
                            pageLimit && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800",
                                children: [
                                    /*#__PURE__*/ _jsx(Ruler, {
                                        className: "h-2.5 w-2.5"
                                    }),
                                    pageLimit.limit,
                                    " ",
                                    pageLimit.unit || 'pages'
                                ]
                            }),
                            volume.dueDate && /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1 text-[11px] text-slate-500",
                                children: [
                                    /*#__PURE__*/ _jsx(Calendar, {
                                        className: "h-3 w-3"
                                    }),
                                    volume.dueDate
                                ]
                            }),
                            meta.formatRules.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                className: "hidden items-center gap-1 text-[10px] text-blue-600 sm:flex",
                                children: [
                                    /*#__PURE__*/ _jsx(Info, {
                                        className: "h-3 w-3"
                                    }),
                                    "From solicitation"
                                ]
                            })
                        ]
                    }),
                    sourceAttachmentId && /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: onLoadTemplate,
                        disabled: loadingTemplate,
                        className: "ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50",
                        title: "Reload template from attachment",
                        children: [
                            loadingTemplate ? /*#__PURE__*/ _jsx(Loader2, {
                                className: "h-3 w-3 animate-spin"
                            }) : /*#__PURE__*/ _jsx(Download, {
                                className: "h-3 w-3"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "hidden sm:inline",
                                children: "Reload template"
                            })
                        ]
                    })
                ]
            }),
            showMeta && /*#__PURE__*/ _jsx("div", {
                className: "border-t border-slate-100 bg-slate-50/50 px-3 py-4 sm:px-4",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "mx-auto max-w-5xl",
                    children: /*#__PURE__*/ _jsx(VolumeMetadataPanel, {
                        projectId: projectId,
                        volume: volume
                    })
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Shared small components
// ---------------------------------------------------------------------------
function LoadTemplateCard({ onLoad, loading }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-4 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3",
        children: [
            /*#__PURE__*/ _jsx(FileSpreadsheet, {
                className: "h-5 w-5 flex-shrink-0 text-blue-600"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-sm font-medium text-blue-900",
                        children: "Government pricing template available"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-blue-700/70",
                        children: "Load the attached XLSX with merged cells, column widths, and formatting intact."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: onLoad,
                disabled: loading,
                className: "inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50",
                children: [
                    loading ? /*#__PURE__*/ _jsx(Loader2, {
                        className: "h-3.5 w-3.5 animate-spin"
                    }) : /*#__PURE__*/ _jsx(Download, {
                        className: "h-3.5 w-3.5"
                    }),
                    "Load template"
                ]
            })
        ]
    });
}
function ResultToast({ message }) {
    return /*#__PURE__*/ _jsx("div", {
        className: `mt-2 rounded-lg border px-3 py-2 text-xs font-medium ${message.startsWith('Error') ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`,
        children: message
    });
}
